'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const challenges = [
  {
    tag: '[01]',
    title: 'SOC 2 as a Sales Requirement',
    desc: 'Enterprise customers demand SOC 2 Type II before signing. Most SaaS companies treat it as a one-time project. TechSonar builds continuous compliance that satisfies auditors every year without the annual scramble.',
  },
  {
    tag: '[02]',
    title: 'Velocity vs. Security',
    desc: 'SaaS teams ship multiple times a day. Security cannot be a gate at the end of the pipeline, it must be embedded into every sprint, every PR, every deployment.',
  },
  {
    tag: '[03]',
    title: 'Multi-Tenant Architecture Risk',
    desc: 'Tenant isolation failures, privilege escalation, and insecure API design are the most common critical vulnerabilities in SaaS products. These are not caught by generic scanners.',
  },
  {
    tag: '[04]',
    title: 'Cloud Cost vs. Security Posture',
    desc: 'Misconfigured S3 buckets, over-permissive IAM roles, and unencrypted data stores are endemic in fast-moving SaaS companies. IaC security and cloud posture management close these gaps at scale.',
  },
];

const services = [
  { label: 'SOC 2 Continuous Compliance', href: '/services/compliance', desc: 'Engineering-led SOC 2 Type II programmes. Continuous evidence collection, control monitoring, and embedded support through every audit window.' },
  { label: 'AppSec & Vulnerability Management', href: '/services/appsec', desc: 'SAST, DAST, and SCA embedded into your CI/CD pipelines. Business-logic vulnerabilities in multi-tenant SaaS that automated scanners miss.' },
  { label: 'Continuous DevSecOps', href: '/services/devsecops', desc: 'Senior TechSonar engineers working inside your team, in your sprints, your standups, your delivery process. Security at engineering speed.' },
  { label: 'IaC & Pipeline Security', href: '/services/iac-pipeline', desc: 'Misconfigured infrastructure caught before it is ever provisioned. Policy-as-code, secrets detection, SBOM generation, and supply chain security.' },
  { label: 'Cloud & Platform Engineering', href: '/services/cloud-platform', desc: 'Secure AWS, Azure, and GCP architectures. IAM hardening, network segmentation, container security, and cloud-native security controls.' },
  { label: 'Accelerator Programme', href: '/services/accelerator', desc: 'Rapid SOC 2 readiness ahead of a major enterprise deal, funding round, or customer security review. Senior engineers embedded, doing the work at pace.' },
];

const outcomes = [
  { metric: 'SOC 2', label: 'Type II achieved and maintained continuously' },
  { metric: 'ISO 27001', label: 'Certification alongside SOC 2, one programme' },
  { metric: 'SDLC', label: 'Security embedded from design to deployment' },
  { metric: 'Cloud', label: 'Posture management across AWS, Azure, GCP' },
];

export default function SaasTechPage() {
  useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#050a0f] text-[#f5f5f8] noise-overlay-animated">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="orb orb-blue" style={{ width: '600px', height: '600px', top: '-150px', left: '-100px', opacity: 0.3 }} />
          <div className="orb orb-teal" style={{ width: '400px', height: '400px', bottom: '0', right: '5%', opacity: 0.2 }} />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-6 fade-in-up">
            <Link href="/" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Home</Link>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest">Industries</span>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">SaaS & Technology</span>
          </div>
          <div className="max-w-[900px]">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4 fade-in-up" style={{ transitionDelay: '0.05s' }}>
              [SaaS & Technology]
            </p>
            <h1 className="font-heading text-[clamp(2.5rem,6vw,4.5rem)] text-[#f5f5f8] font-medium leading-[1.05] mb-6 fade-in-up" style={{ transitionDelay: '0.1s' }}>
              Security that ships<br />
              <span className="text-[#80edfa]">at engineering speed.</span>
            </h1>
            <p className="text-[#aaa] text-lg leading-relaxed max-w-[680px] fade-in-up" style={{ transitionDelay: '0.15s' }}>
              SOC 2, AppSec, DevSecOps, and cloud security for SaaS companies that cannot afford to slow down. TechSonar embeds senior engineers into your team to build security into the delivery process, not bolt it on at the end.
            </p>
          </div>
        </div>
      </section>

      {/* Outcomes strip */}
      <section className="border-t border-[#222] py-12 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {outcomes?.map((o, i) => (
              <div key={i} className="fade-in-up" style={{ transitionDelay: `${i * 0.07}s` }}>
                <p className="font-heading text-2xl text-[#80edfa] font-medium mb-2">{o?.metric}</p>
                <p className="text-xs text-[#555] leading-relaxed">{o?.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">The challenge</p>
            <h2 className="font-heading text-[clamp(1.8rem,4vw,3rem)] text-[#f5f5f8] font-medium leading-tight max-w-[600px]">
              What makes SaaS security hard.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#222]">
            {challenges?.map((c, i) => (
              <div key={i} className="bg-[#050a0f] p-8 lg:p-10 fade-in-up" style={{ transitionDelay: `${i * 0.07}s` }}>
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">{c?.tag}</p>
                <h3 className="font-heading text-xl text-[#f5f5f8] font-medium mb-3">{c?.title}</h3>
                <p className="text-sm text-[#666] leading-relaxed">{c?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="border-t border-[#222] py-20 lg:py-28 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">How we help</p>
            <h2 className="font-heading text-[clamp(1.8rem,4vw,3rem)] text-[#f5f5f8] font-medium leading-tight max-w-[600px]">
              Services built for SaaS and tech companies.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#222]">
            {services?.map((s, i) => (
              <Link
                key={i}
                href={s?.href}
                className="group bg-[#0a0f14] p-8 hover:bg-[#111519] transition-colors duration-300 fade-in-up"
                style={{ transitionDelay: `${i * 0.07}s` }}
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="font-heading text-lg text-[#f5f5f8] font-medium group-hover:text-[#80edfa] transition-colors pr-4">{s?.label}</h3>
                  <svg className="w-4 h-4 text-[#333] group-hover:text-[#80edfa] transition-colors flex-shrink-0 mt-1" viewBox="0 0 16 16" fill="none">
                    <path d="M3 13L13 3M13 3H6M13 3V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <p className="text-sm text-[#555] leading-relaxed group-hover:text-[#888] transition-colors">{s?.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="max-w-[700px] fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">[Get started]</p>
            <h2 className="font-heading text-[clamp(2rem,4vw,3.5rem)] text-[#f5f5f8] font-medium leading-tight mb-6">
              Win enterprise deals. Ship securely. Scale with confidence.
            </h2>
            <p className="text-[#aaa] text-lg leading-relaxed mb-8">
              Whether you need SOC 2 for a major customer, AppSec embedded into your pipeline, or continuous DevSecOps, TechSonar delivers at engineering speed.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link href="/contact" className="btn-primary">Book a discovery call</Link>
              <Link href="/services/devsecops" className="px-6 py-3 border border-[#333] text-[#aaa] text-sm uppercase tracking-wide hover:border-[#80edfa] hover:text-[#80edfa] transition-colors font-sans">
                View DevSecOps Service
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
